"use client";

import React from "react";
import Flex from "antd/es/flex";
import Text from "antd/es/typography/Text";

/**
 * The loader mark never follows the OS color scheme (Oct 2026). The dashboard
 * and staff have no dark theme, so a `prefers-color-scheme: dark` swap to
 * `/symbol-white.png` painted a white mark on a white surface and the arc
 * spun around nothing (#649). A caller on a dark background opts in with
 * `useDarkSymbol` instead, the same way `OpeningsLogo` works (#572).
 */
export interface LoadingIndicatorProps {
  /** Loader size. @default "default" */
  size?: "small" | "default" | "large";
  /**
   * Text shown beneath the loader. Pass `null` to render the mark alone.
   * @default "Loading"
   */
  label?: React.ReactNode;
  /**
   * Minimum height of the centering container. Use for page- and card-level
   * loading states so the loader sits in the vertical center.
   */
  minHeight?: number | string;
  /** Padding around the centered content. @default 24 */
  padding?: number | string;
  /**
   * Color of the rotating arc around the logo mark.
   * @default "rgba(0, 0, 0, 0.88)"
   */
  indicatorColor?: string;
  /** Brand symbol source for a light surface. @default "/symbol.png" */
  symbolSrc?: string;
  /** What `useDarkSymbol` renders. @default "/symbol-white.png" */
  symbolDarkSrc?: string;
  /**
   * Render the white mark, for a loader on a dark background. The only way
   * `symbolDarkSrc` reaches the page.
   * @default false
   */
  useDarkSymbol?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const MARK_SIZE: Record<NonNullable<LoadingIndicatorProps["size"]>, number> = {
  small: 30,
  default: 44,
  large: 60,
};

const STYLE_ELEMENT_ID = "openings-logo-loader-styles";

const LOADER_STYLES = `
.openings-logo-loader {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.openings-logo-loader__ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: openings-logo-loader-spin 1.1s linear infinite;
}
.openings-logo-loader__ring circle {
  fill: none;
  stroke: currentColor;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 34.5 138;
  opacity: 0.85;
}
.openings-logo-loader__mark {
  display: inline-flex;
  width: 56%;
  height: 56%;
  animation: openings-logo-loader-breathe 1.6s ease-in-out infinite;
}
.openings-logo-loader__mark img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
@keyframes openings-logo-loader-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes openings-logo-loader-breathe {
  0%,
  100% {
    transform: scale(0.84);
    opacity: 0.65;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .openings-logo-loader__ring,
  .openings-logo-loader__mark {
    animation: none;
  }
}
`;

// Critical visual styling applied inline so the loader renders correctly on the
// very first paint, before the (animation-only) stylesheet parses. Previously
// the ring's <circle> defaulted to a solid black fill and the mark was
// unconstrained, causing a large black blob to flash then snap to the proper
// ring + logo. Animations still come from LOADER_STYLES.
const RING_STYLE: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
};

const RING_CIRCLE_STYLE: React.CSSProperties = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 3,
  strokeLinecap: "round",
  strokeDasharray: "34.5 138",
  opacity: 0.85,
};

const MARK_STYLE: React.CSSProperties = {
  display: "inline-flex",
  width: "56%",
  height: "56%",
};

const MARK_IMG_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "contain",
};

/**
 * Standardized loading state: the Openings logo mark breathing inside a thin
 * rotating arc, with an optional label.
 *
 * Shared by the dashboard and staff apps so every loading state uses the same
 * branded animation, label, and sizing. Prefer this (via
 * `DashboardLoadingSpinner` / `StaffLoadingSpinner`) over hand-rolled `<Spin>`
 * blocks. Inline activity indicators (button icons, "Refreshing…" tags) may
 * still use a raw `<Spin>`.
 */
export function LoadingIndicator({
  size = "default",
  label = "Loading",
  minHeight,
  padding = 24,
  indicatorColor = "rgba(0, 0, 0, 0.88)",
  symbolSrc = "/symbol.png",
  symbolDarkSrc = "/symbol-white.png",
  useDarkSymbol = false,
  className,
  style,
}: LoadingIndicatorProps) {
  const dimension = MARK_SIZE[size] ?? MARK_SIZE.default;

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={12}
      className={className}
      style={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        minHeight,
        padding,
        ...style,
      }}
    >
      {/* Rendered in JSX (not injected via effect) so the animation CSS ships
          with the first paint. React dedupes by `href`. */}
      <style href={STYLE_ELEMENT_ID} precedence="default">
        {LOADER_STYLES}
      </style>
      <span
        className="openings-logo-loader"
        role="status"
        aria-label={typeof label === "string" ? label : "Loading"}
        style={{
          width: dimension,
          height: dimension,
          color: indicatorColor,
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <svg
          className="openings-logo-loader__ring"
          viewBox="0 0 50 50"
          aria-hidden="true"
          focusable="false"
          style={RING_STYLE}
        >
          <circle cx="25" cy="25" r="22" style={RING_CIRCLE_STYLE} />
        </svg>
        <span className="openings-logo-loader__mark" style={MARK_STYLE}>
          <img
            src={useDarkSymbol ? symbolDarkSrc : symbolSrc}
            alt=""
            style={MARK_IMG_STYLE}
          />
        </span>
      </span>
      {label != null ? (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Text>
      ) : null}
    </Flex>
  );
}

export default LoadingIndicator;
